import { FC, ReactNode } from 'react';
import {
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  type ButtonProps,
  IconButton,
  Breakpoint,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Column, Row } from '../Layout';
import Close from '@mui/icons-material/Close';
import InfoOutline from '@mui/icons-material/InfoOutline';
import Switch from '../Switch';

const StyledToggleRow = styled(Row)(({ theme }) => ({
  marginRight: theme.spacing(-theme.spacingSizes.xxs),
}));

type Props = {
  actions: {
    confirm: {
      onClick: () => void;
      label?: string;
      variant?: ButtonProps['variant'];
      color?: ButtonProps['color'];
    };
    alternateConfirm?: {
      onClick: () => void;
      label?: string;
      variant?: ButtonProps['variant'];
      color?: ButtonProps['color'];
    };
    toggle?: {
      checked: boolean;
      onChange: (checked: boolean) => void;
      label?: string;
      tooltip?: string;
    };
    cancel?: {
      onClick?: () => void;
      label?: string;
      variant?: ButtonProps['variant'];
      color?: ButtonProps['color'];
    };
  };
  open?: boolean;
  onClose?: () => void;
  title: string;
  content: ReactNode;
  loading?: boolean;
  maxWidth?: Breakpoint;
};

const ConfirmDialog: FC<Props> = ({
  open,
  onClose,
  actions,
  title,
  content,
  loading,
  maxWidth = 'xs',
}) => {
  const confirmAction = {
    label: 'Confirm',
    variant: 'contained',
    color: 'primary',
    ...actions.confirm,
  } as const;
  const cancelAction = {
    label: 'Cancel',
    variant: 'outlined',
    color: 'primary',
    onClick: onClose,
    ...(actions.cancel ?? {}),
  } as const;

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth={maxWidth} fullWidth>
      <DialogTitle>
        <Column spacing="sm" fullWidth>
          <Row centered fullWidth justify="space-between">
            <Typography variant="h4">{title}</Typography>
            <IconButton onClick={onClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Row>
          <Divider />
        </Column>
      </DialogTitle>

      <DialogContent>
        {typeof content === 'string' ? (
          <Typography variant="body2">{content}</Typography>
        ) : (
          content
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant={cancelAction.variant}
          color={cancelAction.color}
          size="large"
          onClick={cancelAction.onClick}
          disabled={loading}
        >
          {cancelAction.label}
        </Button>
        <Row spacing="sm">
          {actions.toggle ? (
            <StyledToggleRow centered>
              <Tooltip title={actions.toggle?.tooltip}>
                <span>
                  <Row spacing="xxs" centered>
                    <Typography
                      variant="h6"
                      color={loading ? 'grey.500' : 'grey.900'}
                    >
                      {actions.toggle?.label}
                    </Typography>
                    {actions.toggle?.tooltip ? (
                      <InfoOutline fontSize="inherit" />
                    ) : null}
                  </Row>
                </span>
              </Tooltip>
              <Switch
                checked={actions.toggle.checked}
                onChange={actions.toggle.onChange}
                disabled={loading}
              />
            </StyledToggleRow>
          ) : null}
          {actions.alternateConfirm ? (
            <Button
              variant={actions.alternateConfirm.variant ?? 'outlined'}
              color={actions.alternateConfirm.color}
              size="large"
              onClick={actions.alternateConfirm.onClick}
              loading={loading}
            >
              {actions.alternateConfirm.label}
            </Button>
          ) : null}
          <Button
            variant={confirmAction.variant}
            color={confirmAction.color}
            size="large"
            onClick={confirmAction.onClick}
            loading={loading}
          >
            {confirmAction.label}
          </Button>
        </Row>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
